import { enrichedModelSchema, } from '../interfaces/enrichedModuleSchema.js';
import { EnrichedColumn } from './EnrichedColumn.js';
import { EnrichedRelation } from './EnrichedRelation.js';
import { pascalCaseToKebab, pascalCaseToLabel, pascalToSnakeCase } from '../utils/strings.js';
import { validateFrameworkTable } from '../utils/validateFrameworkTables.js';
export class EnrichedModel {
    parentModule;
    name;
    namePlural;
    table;
    route;
    title;
    subtitle;
    displayName;
    displayNamePlural;
    columns;
    relations;
    constructor(params, parentModule) {
        this.parentModule = parentModule;
        validateFrameworkTable(params.table, params.name);
        this.name = params.name;
        this.namePlural = params.namePlural;
        this.table = params.table;
        this.route = params.route;
        this.title = params.title;
        this.subtitle = params.subtitle;
        this.displayName = params.displayName;
        this.displayNamePlural = params.displayNamePlural;
        // TODO: Auto order columns or add a helper to get an array of columns since object
        // order is not guaranteed
        //
        // Convert columns to EnrichedColumnSchema instances
        this.columns = {};
        for (const [key, column] of Object.entries(params.columns)) {
            const newColumn = column instanceof EnrichedColumn ? column : new EnrichedColumn(column, this);
            newColumn.parentModel = this;
            this.columns[key] = newColumn;
            if (this.columns[key].name !== key) {
                throw new ColumnNameMismatchError("Column name doesn't match column key", this.columns[key]);
            }
        }
        // TODO: Auto order relations or add a helper to get an array of columns since object
        // order is not guaranteed
        //
        // Convert relations to EnrichedRelationSchema instances
        this.relations = {};
        for (const [key, relation] of Object.entries(params.relations)) {
            const newRelation = relation instanceof EnrichedRelation ? relation : new EnrichedRelation(relation, this);
            newRelation.parentModel = this;
            this.relations[key] = newRelation;
            if (this.relations[key].name !== key) {
                throw new RelationNameMismatchError("Relation name doesn't match relation key", this.relations[key]);
            }
            this.relations[key].parentModel = this;
        }
    }
    /**
     * Creates an EnrichedModel instance directly from a JSON string.
     *
     * This method parses the provided JSON string, which should represent an enriched model object,
     * and constructs a new EnrichedModel instance using the parsed data. Note that this method
     * does not perform schema validation; it assumes the JSON is well-formed and matches the
     * expected structure.
     *
     * @param json - A JSON string representing an enriched model object.
     * @returns An EnrichedModel instance populated with the parsed data.
     * @throws If the JSON is malformed or cannot be parsed.
     *
     * @example
     * ```typescript
     * const json = '{"name":"User","namePlural":"Users","table":"users","route":"users","title":"User","subtitle":"","displayName":"User","displayNamePlural":"Users","columns":{},"relations":{}}'
     * const model = EnrichedModel.fromJSON(json)
     * ```
     */
    static fromJSON(json) {
        return new EnrichedModel(JSON.parse(json));
    }
    /**
     * Safely creates an EnrichedModel instance from a JSON string.
     *
     * This method parses the provided JSON string and validates its structure against the
     * enriched model schema. If the JSON is invalid or does not conform to the expected
     * schema, an error is thrown. This provides type safety and ensures data integrity.
     *
     * @param json - A JSON string representing an enriched model object.
     * @returns An EnrichedModel instance populated with validated data.
     * @throws If the JSON is malformed or does not match the schema.
     *
     * @example
     * ```typescript
     * const json = '{"name":"User","namePlural":"Users","table":"users","route":"users","title":"User","subtitle":"","displayName":"User","displayNamePlural":"Users","columns":{},"relations":{}}'
     * const model = EnrichedModel.fromJSONSafe(json)
     * ```
     */
    static fromJSONSafe(json) {
        return new EnrichedModel(enrichedModelSchema.parse(JSON.parse(json)));
    }
    /**
     * Converts the EnrichedModel instance into a plain JavaScript object (POJO).
     *
     * This method transforms the model and all its associated columns and relations into
     * plain objects, stripping away class methods and returning only the data structure.
     * This is useful for serialization, transport, or storage operations.
     *
     * @returns A plain object representing the enriched model with all its data.
     *
     * @example
     * ```typescript
     * const model = new EnrichedModel({...})
     * const pojo = model.toPOJO()
     * // Returns: { name: 'User', namePlural: 'Users', table: 'users', route: 'users', ... }
     * ```
     */
    toPOJO() {
        return {
            name: this.name,
            namePlural: this.namePlural,
            table: this.table,
            route: this.route,
            title: this.title,
            subtitle: this.subtitle,
            displayName: this.displayName,
            displayNamePlural: this.displayNamePlural,
            columns: Object.entries(this.columns).reduce((output, [key, column]) => {
                output[key] = column.toPOJO();
                return output;
            }, {}),
            relations: Object.entries(this.relations).reduce((output, [key, relation]) => {
                output[key] = relation.toPOJO();
                return output;
            }, {}),
        };
    }
    /**
     * Serializes the EnrichedModel instance to a JSON string.
     *
     * This method converts the model and all its associated columns and relations to
     * plain objects before stringifying them into JSON format. This is useful for
     * transport, storage, or API responses.
     *
     * @returns A JSON string representation of the enriched model.
     *
     * @example
     * ```typescript
     * const model = new EnrichedModel({...})
     * const json = model.toJSON()
     * // Returns: '{"name":"User","namePlural":"Users","table":"users","route":"users",...}'
     * ```
     */
    toJSON() {
        return JSON.stringify(this.toPOJO());
    }
    /**
     * Generates a display name from a model name.
     *
     * This utility method creates a human-readable display name from the provided
     * model name. By default, it returns the name as-is, but this can be extended
     * to apply formatting or transformation rules as needed.
     *
     * @param name - The model name to convert to a display name.
     * @returns The display name for the model.
     *
     * @example
     * ```typescript
     * const displayName = EnrichedModel.nameToDisplayName('User')
     * // Returns: 'User'
     * ```
     */
    static nameToDisplayName(name) {
        return pascalCaseToLabel(name);
    }
    /**
     * Generates a plural display name from a model name.
     *
     * This utility method creates a human-readable plural display name from the provided
     * model name. By default, it appends 's' to the name, but this can be extended
     * to handle more complex pluralization rules as needed.
     *
     * @param name - The model name to convert to a plural display name.
     * @returns The plural display name for the model.
     *
     * @example
     * ```typescript
     * const displayNamePlural = EnrichedModel.nameToDisplayNamePlural('User')
     * // Returns: 'Users'
     * ```
     */
    static nameToDisplayNamePlural(name) {
        return pascalCaseToLabel(EnrichedModel.nameToPlural(name));
    }
    /**
     * Converts a singular model name to its plural form.
     *
     * This utility method transforms a singular model name into its plural form.
     * By default, it appends 's' to the name, but this can be extended to handle
     * more complex pluralization rules for irregular nouns.
     *
     * @param name - The singular model name to pluralize.
     * @returns The plural form of the model name.
     *
     * @example
     * ```typescript
     * const plural = EnrichedModel.nameToPlural('User')
     * // Returns: 'Users'
     * ```
     */
    static nameToPlural(name) {
        return name + 's';
    }
    /**
     * Converts a model name to its corresponding database table name.
     *
     * This utility method transforms a PascalCase model name into a snake_case
     * table name by first pluralizing the name and then converting it to snake_case
     * format, which is the standard convention for database table names.
     *
     * @param name - The model name to convert to a table name.
     * @returns The snake_case table name.
     *
     * @example
     * ```typescript
     * const tableName = EnrichedModel.nameToTable('User')
     * // Returns: 'users'
     * ```
     */
    static nameToTable(name) {
        return pascalToSnakeCase(EnrichedModel.nameToPlural(name));
    }
    /**
     * Converts a model name to its corresponding route name.
     *
     * This utility method transforms a PascalCase model name into a kebab-case
     * route name by first pluralizing the name and then converting it to kebab-case
     * format, which is the standard convention for URL routes.
     *
     * @param name - The model name to convert to a route name.
     * @returns The kebab-case route name.
     *
     * @example
     * ```typescript
     * const routeName = EnrichedModel.nameToRoute('User')
     * // Returns: 'users'
     * ```
     */
    static nameToRoute(name) {
        return pascalCaseToKebab(EnrichedModel.nameToPlural(name));
    }
    /**
     * Sets all the names, table, route, and display names from the model name.
     *
     * This method updates all name-related properties of the model based on the provided
     * name, including the singular and plural forms, table name, route name, and display
     * names. It uses the utility methods to ensure consistent naming conventions.
     *
     * WARNING: In almost all cases you should use EnrichedModule.setNames() as
     * it updates other modules and the registry. This method only updates the model
     * itself and does not propagate changes to related modules or the module registry.
     *
     * @param name - The new model name to set all derived names from.
     *
     * @example
     * ```typescript
     * const model = new EnrichedModel({...})
     * model.setNames('User')
     * // Results in:
     * // model.name = 'User'
     * // model.namePlural = 'Users'
     * // model.table = 'users'
     * // model.route = 'users'
     * // model.displayName = 'User'
     * // model.displayNamePlural = 'Users'
     * ```
     */
    // TODO: We should support name in any case style
    setNames(name) {
        const table = EnrichedModel.nameToTable(name);
        validateFrameworkTable(table, name);
        this.name = name;
        this.namePlural = EnrichedModel.nameToPlural(name);
        this.table = table;
        this.route = EnrichedModel.nameToRoute(name);
        this.displayName = EnrichedModel.nameToDisplayName(name);
        this.displayNamePlural = EnrichedModel.nameToDisplayNamePlural(name);
    }
}
export class ColumnNameMismatchError extends Error {
    column;
    constructor(message, column) {
        super(message);
        this.column = column;
        this.name = 'ColumnNameMismatchError';
    }
}
export class RelationNameMismatchError extends Error {
    column;
    constructor(message, column) {
        super(message);
        this.column = column;
        this.name = 'RelationNameMismatchError';
    }
}
